import React, { useState } from 'react'
import { Image, Typography, Space, Tag, Button, Modal, message } from 'antd'
import { FileImageOutlined, SearchOutlined, LinkOutlined, CopyOutlined, EyeOutlined } from '@ant-design/icons'

const { Paragraph, Text } = Typography

interface MessageContentProps {
  content: string | any[]
  role: 'user' | 'assistant' | 'system'
  isCanvasNode?: boolean
  resultId?: string
}

/**
 * 🔥 消息内容渲染组件
 * ✅ 支持图片、搜索等高级功能展示
 * ✅ 兼容OpenAI多模态消息格式
 * ✅ 支持Canvas节点特殊显示
 */
const MessageContent: React.FC<MessageContentProps> = ({
  content,
  role,
  isCanvasNode = false,
  resultId
}) => {
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewContent, setPreviewContent] = useState('')
  const [previewLanguage, setPreviewLanguage] = useState('')

  // 🔥 新增：复制代码块功能
  const copyCodeBlock = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      message.success('代码已复制到剪贴板')
    } catch (error) {
      message.error('复制失败')
    }
  }

  // 🔥 新增：预览代码块功能
  const previewCodeBlock = (code: string, language: string) => {
    setPreviewContent(code)
    setPreviewLanguage(language)
    setPreviewVisible(true)
  }

  // 🔥 新增：最大化预览功能
  const openMaximizedPreview = (code: string, language: string) => {
    const newWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes')
    if (newWindow) {
      if (language.toLowerCase() === 'html' || language.toLowerCase() === 'htm') {
        // HTML直接渲染
        const blob = new Blob([code], { type: 'text/html' })
        const url = URL.createObjectURL(blob)
        newWindow.location.href = url
        // 清理URL对象
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      } else {
        // 其他代码显示源码
        const htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>代码预览 - ${language}</title>
            <style>
              body { font-family: 'Courier New', monospace; margin: 20px; background: #f6f8fa; }
              pre { background: white; padding: 20px; border-radius: 8px; overflow: auto; border: 1px solid #d0d7de; }
              .header { background: #24292f; color: white; padding: 10px 20px; margin: -20px -20px 20px -20px; border-radius: 8px 8px 0 0; }
            </style>
          </head>
          <body>
            <div class="header">
              <h3 style="margin: 0;">代码预览 - ${language}</h3>
            </div>
            <pre><code>${code.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>
          </body>
          </html>
        `
        const blob = new Blob([htmlContent], { type: 'text/html' })
        const url = URL.createObjectURL(blob)
        newWindow.location.href = url
        // 清理URL对象
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      }
    }
  }

  // 🔥 新增：检测和渲染代码块
  const renderCodeBlocks = (text: string) => {
    // 匹配代码块模式：```language\ncode\n```
    const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g
    const parts = []
    let lastIndex = 0
    let match

    while ((match = codeBlockRegex.exec(text)) !== null) {
      // 添加代码块前的文本
      if (match.index > lastIndex) {
        const beforeText = text.slice(lastIndex, match.index)
        if (beforeText.trim()) {
          parts.push(
            <Paragraph key={`text-${lastIndex}`} style={{ whiteSpace: 'pre-wrap', margin: '0 0 8px 0' }}>
              {beforeText}
            </Paragraph>
          )
        }
      }

      const language = match[1] || 'text'
      const code = match[2].trim()

      // 添加代码块
      parts.push(
        <div key={`code-${match.index}`} style={{ margin: '8px 0', border: '1px solid #d9d9d9', borderRadius: '6px' }}>
          <div style={{
            background: '#fafafa',
            padding: '8px 12px',
            borderBottom: '1px solid #d9d9d9',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Tag color="blue">{language}</Tag>
            <Space>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyCodeBlock(code)}
              >
                复制代码
              </Button>
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => previewCodeBlock(code, language)}
              >
                预览
              </Button>
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => openMaximizedPreview(code, language)}
                type="primary"
              >
                最大化
              </Button>
            </Space>
          </div>
          <pre style={{
            margin: 0,
            padding: '12px',
            background: '#f6f8fa',
            overflow: 'auto',
            fontSize: '13px',
            lineHeight: '1.4'
          }}>
            <code>{code}</code>
          </pre>
        </div>
      )

      lastIndex = match.index + match[0].length
    }

    // 添加最后剩余的文本
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex)
      if (remainingText.trim()) {
        parts.push(
          <Paragraph key={`text-${lastIndex}`} style={{ whiteSpace: 'pre-wrap', margin: '0 0 8px 0' }}>
            {remainingText}
          </Paragraph>
        )
      }
    }

    return parts.length > 0 ? parts : [
      <Paragraph key="default" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
        {text}
      </Paragraph>
    ]
  }
  // 🔥 处理多模态内容（OpenAI格式）
  if (Array.isArray(content)) {
    return (
      <div>
        {content.map((item, index) => {
          if (typeof item === 'object' && item !== null) {
            // 文本内容
            if (item.type === 'text') {
              return (
                <div key={index}>
                  {renderCodeBlocks(item.text)}
                </div>
              )
            }
            
            // 图片内容
            if (item.type === 'image_url') {
              const imageUrl = item.image_url?.url
              if (imageUrl) {
                return (
                  <div key={index} style={{ margin: '8px 0' }}>
                    <Space direction="vertical" size="small">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FileImageOutlined style={{ color: '#1890ff' }} />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          图片附件
                        </Text>
                      </div>
                      <Image
                        src={formatImageUrl(imageUrl)}
                        alt="消息图片"
                        style={{ maxWidth: '300px', maxHeight: '200px' }}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                        preview={{
                          mask: <div style={{ color: 'white' }}>预览图片</div>
                        }}
                      />
                    </Space>
                  </div>
                )
              }
            }
            
            // 搜索结果内容
            if (item.type === 'search_result') {
              return (
                <div key={index} style={{ margin: '8px 0', padding: '8px', background: '#f6f8fa', borderRadius: '4px' }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <SearchOutlined style={{ color: '#52c41a' }} />
                      <Text strong style={{ fontSize: '12px' }}>
                        搜索结果
                      </Text>
                    </div>
                    {item.title && (
                      <Text strong>{item.title}</Text>
                    )}
                    {item.snippet && (
                      <Paragraph style={{ margin: 0, fontSize: '13px' }}>
                        {item.snippet}
                      </Paragraph>
                    )}
                    {item.url && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <LinkOutlined style={{ fontSize: '12px', color: '#1890ff' }} />
                        <Text 
                          style={{ fontSize: '12px', color: '#1890ff' }}
                          copyable={{ text: item.url }}
                        >
                          {item.url}
                        </Text>
                      </div>
                    )}
                  </Space>
                </div>
              )
            }
          }
          
          // 兜底：直接显示字符串内容
          return (
            <Paragraph 
              key={index} 
              style={{ whiteSpace: 'pre-wrap', margin: '0 0 8px 0' }}
            >
              {String(item)}
            </Paragraph>
          )
        })}
        
        {/* 🔥 Canvas节点标识 */}
        {isCanvasNode && resultId && (
          <div style={{ marginTop: '8px' }}>
            <Tag color="purple" style={{ fontSize: '11px' }}>
              Canvas节点: {resultId.substring(0, 12)}...
            </Tag>
          </div>
        )}
      </div>
    )
  }

  // 🔥 处理字符串内容，检测是否包含特殊格式
  const stringContent = String(content)
  
  // 检测JSON格式的多模态内容
  if (stringContent.trim().startsWith('[') || stringContent.trim().startsWith('{')) {
    try {
      const parsedContent = JSON.parse(stringContent)
      if (Array.isArray(parsedContent)) {
        return <MessageContent content={parsedContent} role={role} isCanvasNode={isCanvasNode} resultId={resultId} />
      }
    } catch (e) {
      // 解析失败，继续作为普通文本处理
    }
  }

  // 🔥 检测图片URL模式
  const imageUrlPattern = /(https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp|svg))/gi
  const imageMatches = stringContent.match(imageUrlPattern)
  
  if (imageMatches && imageMatches.length > 0) {
    const parts = stringContent.split(imageUrlPattern)
    return (
      <div>
        {parts.map((part, index) => {
          if (imageMatches.includes(part)) {
            return (
              <div key={index} style={{ margin: '8px 0' }}>
                <Space direction="vertical" size="small">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FileImageOutlined style={{ color: '#1890ff' }} />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      图片链接
                    </Text>
                  </div>
                  <Image
                    src={part}
                    alt="消息图片"
                    style={{ maxWidth: '300px', maxHeight: '200px' }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                    preview={{
                      mask: <div style={{ color: 'white' }}>预览图片</div>
                    }}
                  />
                </Space>
              </div>
            )
          } else if (part.trim()) {
            return (
              <Paragraph 
                key={index} 
                style={{ whiteSpace: 'pre-wrap', margin: '0 0 8px 0' }}
              >
                {part}
              </Paragraph>
            )
          }
          return null
        })}
        
        {/* 🔥 Canvas节点标识 */}
        {isCanvasNode && resultId && (
          <div style={{ marginTop: '8px' }}>
            <Tag color="purple" style={{ fontSize: '11px' }}>
              Canvas节点: {resultId.substring(0, 12)}...
            </Tag>
          </div>
        )}
      </div>
    )
  }

  // 🔥 普通文本内容
  return (
    <div>
      {renderCodeBlocks(stringContent)}

      {/* 🔥 Canvas节点标识 */}
      {isCanvasNode && resultId && (
        <div style={{ marginTop: '8px' }}>
          <Tag color="purple" style={{ fontSize: '11px' }}>
            Canvas节点: {resultId.substring(0, 12)}...
          </Tag>
        </div>
      )}

      {/* 🔥 代码预览模态框 - 修复关闭后布局问题 */}
      <Modal
        title={`代码预览 - ${previewLanguage}`}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={() => copyCodeBlock(previewContent)}>
            复制代码
          </Button>,
          <Button
            key="maximize"
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => openMaximizedPreview(previewContent, previewLanguage)}
          >
            最大化预览
          </Button>,
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
        zIndex={1000}
        maskClosable={true}
        destroyOnHidden={true}
        afterClose={() => {
          setPreviewContent('')
          setPreviewLanguage('')
          // 🔥 修复：强制重新渲染页面布局
          setTimeout(() => {
            document.body.style.overflow = ''
            document.documentElement.style.overflow = ''
            // 触发窗口resize事件来修复布局
            window.dispatchEvent(new Event('resize'))
          }, 100)
        }}
        styles={{
          body: {
            maxHeight: '60vh',
            overflow: 'auto'
          }
        }}
      >
        {previewLanguage.toLowerCase() === 'html' || previewLanguage.toLowerCase() === 'htm' ? (
          // HTML代码直接渲染
          <div style={{
            border: '1px solid #d0d7de',
            borderRadius: '6px',
            overflow: 'auto',
            maxHeight: '400px',
            background: 'white',
            isolation: 'isolate' // 🔥 修复：防止样式污染
          }}>
            <div
              dangerouslySetInnerHTML={{ __html: previewContent }}
              style={{
                padding: '16px',
                // 🔥 修复：重置可能影响布局的样式
                width: 'auto',
                height: 'auto',
                position: 'static',
                display: 'block'
              }}
            />
          </div>
        ) : (
          // 其他代码显示源码
          <pre style={{
            background: '#f6f8fa',
            padding: '16px',
            borderRadius: '6px',
            overflow: 'auto',
            maxHeight: '400px',
            fontSize: '13px',
            lineHeight: '1.4',
            margin: 0,
            whiteSpace: 'pre-wrap'
          }}>
            <code>{previewContent}</code>
          </pre>
        )}
      </Modal>
    </div>
  )
}

/**
 * 格式化图片URL
 * 处理Refly存储格式和其他图片URL
 */
function formatImageUrl(url: string): string {
  // 如果是Refly的storageKey格式，使用我们的代理接口
  if (url.startsWith('static/')) {
    return `/api/conversations/images/${url}`
  }

  // 如果已经是完整的URL
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }

  // 如果是相对路径，尝试构建完整URL
  if (url.startsWith('/')) {
    return `https://api.refly.ai${url}`
  }

  return url
}

export default MessageContent
